// Sponsor Ad style editor — a small layout tool for the 6 ad styles.
//
// Coordinate system: everything is stored as PERCENTAGES of the artboard
// (0-100). The backend renderer uses the same units, and the picture on
// the artboard IS the server's render, so what you see is what publishes
// to Facebook.
//
// Screen layout:
//   - Left:   style tabs (bottom_banner, top_banner, …)
//   - Centre: toolbar + artboard
//   - Right:  layers list, "add" buttons, inspector for the selection
//
// How it stays fast (2026-10-06 rework):
//   - Dragging and resizing never touch React state. Pointer moves write
//     straight to the element's style inside requestAnimationFrame, and
//     the layout is committed ONCE, on release. Before, every mouse move
//     re-rendered the whole editor and queued a server render.
//   - While something is being dragged it is drawn in the browser (a
//     "ghost"), and the server picture is re-requested WITHOUT it, so
//     the element follows the pointer instead of trailing a stale image.
//   - A preview that has been overtaken by a newer change is aborted, and
//     a late answer can never overwrite a newer one.
//   - Server side, the logo and image elements are cached in memory and
//     the render runs off the event loop (see backend/asset_cache.py).
//
// All the resize / snap / align / history maths lives in
// `@/lib/adEditorGeometry` and is unit-tested there.
import {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  X, Save, RotateCcw, Loader2, Type, Image as ImageIcon,
  ChevronRight, Copy, Upload, Trash2, Plus, Settings, Square,
  ArrowUpToLine, ArrowDownToLine, ChevronUp, ChevronDown,
  Undo2, Redo2, Lock, Unlock, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import * as G from "@/lib/adEditorGeometry";
import {
  checkPastedSize, coerceImageType, imageFileFromClipboardEvent,
  isEditableTarget, pushPasteTarget,
} from "@/lib/clipboardImage";
import PasteImageButton from "@/components/PasteImageButton";
import AdBannerDialog from "@/components/AdBannerDialog";

// Order matches the reference layouts in the ads_render module.
export const AD_STYLES = [
  { key: "bottom_banner",     label: "1. Bottom Banner" },
  { key: "top_banner",        label: "2. Top Banner" },
  { key: "corner_badge",      label: "3. Corner Badge" },
  { key: "bottom_bar_semi",   label: "4. Bottom Bar (semi)" },
  { key: "side_vertical",     label: "5. Side Vertical" },
  { key: "bottom_left_badge", label: "6. Bottom Left Badge" },
];

const ELEMENT_LABELS = {
  logo: "Logo", title: "Title", subhead: "Subhead",
  phone: "Phone", website: "Website", email: "Email",
  sponsor_bar: "Sponsor bar", sponsor_bar_image: "Sponsor bar (image)",
  image: "Image", text: "Text",
};
// "image_3" → "Image 3", "sponsor_bar_image_2" → "Sponsor bar (image) 2".
function labelFor(key) {
  if (ELEMENT_LABELS[key]) return ELEMENT_LABELS[key];
  const m = String(key || "").match(/^(.*)_(\d+)$/);
  if (m && ELEMENT_LABELS[m[1]]) return `${ELEMENT_LABELS[m[1]]} ${m[2]}`;
  return key;
}
// Elements that can only exist once per style.
const SINGLE_KEYS = ["logo", "title", "subhead", "phone", "website", "email", "sponsor_bar"];
const AD_FIELD_KEYS = ["title", "subhead", "phone", "website", "email"];
const ASSET_ACCEPT = "image/png,image/jpeg,image/webp";
// Marker written to the system clipboard when elements are copied, so a
// picture copied earlier in another app isn't pasted instead.
const CLIPBOARD_MARKER = "newsdesk-ad-elements";

// Fallback family list — replaced at runtime with whatever
// `/api/ads/fonts` reports installed. Slug + display + preview stack.
const FALLBACK_FONT_FAMILIES = [
  { key: "sans",  label: "Sans (system)",  stack: "system-ui, sans-serif" },
  { key: "serif", label: "Serif (system)", stack: "Georgia, serif" },
  { key: "mono",  label: "Mono (system)",  stack: "ui-monospace, monospace" },
];
function familyKeyFromDisplay(display) {
  // Match font_manager._slug reverse: "Playfair Display" -> "playfair-display"
  return String(display || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
const FONT_WEIGHTS = [
  { key: "regular", label: "Regular" },
  { key: "bold", label: "Bold" },
  { key: "italic", label: "Italic" },
  { key: "bolditalic", label: "Bold italic" },
];
const ALIGN_OPTIONS = ["left", "center", "right"];

// Artboard dimensions match the backend's target output width.
const CANVAS_W = G.CANVAS_W;
const CANVAS_H = G.CANVAS_H;

function fileUrl(path) {
  if (!path) return "";
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
}

// Natural pixel size of an image URL, or null if it can't be loaded.
function naturalSize(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const im = new window.Image();
    im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => resolve(null);
    im.src = url;
  });
}

function isLocked(el) {
  return !!(el && el.locked);
}

const TOOL_BTN =
  "text-xs inline-flex items-center gap-1.5 px-2.5 py-1 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40 disabled:hover:border-[var(--border)]";
const TOOL_BTN_ON =
  "text-xs inline-flex items-center gap-1.5 px-2.5 py-1 border border-[var(--ink)] bg-[var(--ink)] text-white";
const MINI_BTN =
  "text-[10px] px-1.5 py-0.5 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[var(--border)]";

export default function AdStyleEditor({ ad, onClose }) {
  const qc = useQueryClient();
  const [style, setStyle] = useState(AD_STYLES[0].key);
  const styleRef = useRef(style);
  styleRef.current = style;
  const [fontsModalOpen, setFontsModalOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const modalOpenRef = useRef(false);
  modalOpenRef.current = fontsModalOpen || bannerOpen;
  // Local view preferences — never persisted, so a shared machine
  // doesn't inherit someone else's.
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [smartGuides, setSmartGuides] = useState(true);
  const [, bump] = useReducer((n) => n + 1, 0);

  // Available font families come from the backend so the list stays in
  // sync as editors install / uninstall Google Fonts.
  const fontsQ = useQuery({
    queryKey: ["ad-fonts"],
    queryFn: () => api.listFonts(),
  });
  const fontFamilies = useMemo(() => {
    const installed = (fontsQ.data || []).map((f) => ({
      key: familyKeyFromDisplay(f.family),
      label: f.family,
      stack: `'${f.family}', system-ui, sans-serif`,
      weights: f.weights || [],
    }));
    // De-dupe against fallback list.
    const seen = new Set(installed.map((f) => f.key));
    return [
      ...installed,
      ...FALLBACK_FONT_FAMILIES.filter((f) => !seen.has(f.key)),
    ];
  }, [fontsQ.data]);

  // ---- the ad's own text fields ------------------------------------
  // Typing in the inspector updates the local draft at once; the save
  // to the server is debounced, and the artboard is re-rendered when
  // that save lands (`adRev`) — the server draws the text from the
  // stored ad, so an earlier render would show the old wording.
  const [adDraft, setAdDraft] = useState(() => ({ ...ad }));
  const [adRev, setAdRev] = useState(0);
  const patchAdMutation = useMutation({
    mutationFn: (patch) => api.patchAd(ad.id, patch),
    onSuccess: () => {
      setAdRev((n) => n + 1);
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save the text"),
  });
  const adPatchRef = useRef({ timer: null, patch: {} });
  const flushAdPatch = useCallback((viaMutation = true) => {
    const pending = adPatchRef.current;
    clearTimeout(pending.timer);
    pending.timer = null;
    const patch = pending.patch;
    pending.patch = {};
    if (!Object.keys(patch).length) return;
    if (viaMutation) patchAdMutation.mutate(patch);
    else api.patchAd(ad.id, patch).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad.id]);
  const onAdChange = useCallback((patch) => {
    setAdDraft((prev) => ({ ...prev, ...patch }));
    const pending = adPatchRef.current;
    pending.patch = { ...pending.patch, ...patch };
    clearTimeout(pending.timer);
    pending.timer = setTimeout(() => flushAdPatch(true), 450);
  }, [flushAdPatch]);
  // Closing mid-debounce must not lose the last keystrokes.
  useEffect(() => () => flushAdPatch(false), [flushAdPatch]);

  // ---- layouts, one per style --------------------------------------
  // `layoutsRef` is the source of truth and is updated synchronously;
  // the state copy only exists to re-render. Keyboard and pointer
  // handlers read the ref, so they never act on a stale layout.
  const [layouts, setLayoutsState] = useState(() => {
    const seed = {};
    for (const s of AD_STYLES) {
      if (ad.styles && ad.styles[s.key]) seed[s.key] = ad.styles[s.key];
    }
    return seed;
  });
  const layoutsRef = useRef(layouts);
  const writeLayouts = useCallback((next) => {
    layoutsRef.current = next;
    setLayoutsState(next);
  }, []);
  const [selection, setSelectionState] = useState([]);
  const selectionRef = useRef(selection);
  const setSelection = useCallback((next) => {
    selectionRef.current = next;
    setSelectionState(next);
  }, []);
  // Undo / redo, kept per style so each tab has its own history.
  const historyRef = useRef({});
  const [dirty, setDirtyState] = useState({});
  const dirtyRef = useRef(dirty);
  const setDirty = useCallback((styleKey, value) => {
    if (!!dirtyRef.current[styleKey] === value) return;
    dirtyRef.current = { ...dirtyRef.current, [styleKey]: value };
    setDirtyState(dirtyRef.current);
  }, []);
  // Tracks styles saved during this editor session so the "SAVED" pill
  // updates immediately, without waiting for a refetch of the ads list.
  const [localSaved, setLocalSaved] = useState({});
  // Copied elements. Survives switching style tabs, so a group can be
  // copied from one style and pasted into another.
  const clipboardRef = useRef([]);

  // Load defaults for styles the ad hasn't customised yet. React Query
  // v5 removed `onSuccess` from useQuery, so we sync the result via a
  // useEffect keyed on `data + style`.
  const defaultQ = useQuery({
    queryKey: ["ad-style-default", style],
    queryFn: () => api.getAdStyleDefault(style),
    enabled: !layouts[style],
  });
  useEffect(() => {
    if (defaultQ.data && !layoutsRef.current[style]) {
      writeLayouts({ ...layoutsRef.current, [style]: defaultQ.data });
    }
  }, [defaultQ.data, style, writeLayouts]);
  const layout = layouts[style];
  const elements = layout?.elements || [];
  const selectedEls = elements.filter((e) => selection.includes(e.key));
  const selectedEl = selectedEls.length === 1 ? selectedEls[0] : null;

  // Every edit goes through `commit`: it records the layout as it was
  // (for undo), swaps in the new one and marks the style unsaved.
  // `tag` groups rapid changes of one kind into a single undo step.
  const commit = useCallback((producer, tag = null) => {
    const s = styleRef.current;
    const cur = layoutsRef.current[s];
    if (!cur) return;
    const next = typeof producer === "function" ? producer(cur) : { ...cur, ...producer };
    if (!next || next === cur) return;
    historyRef.current[s] = G.pushHistory(historyRef.current[s], cur, tag);
    writeLayouts({ ...layoutsRef.current, [s]: next });
    setDirty(s, true);
    bump();
  }, [writeLayouts, setDirty]);
  const commitElements = useCallback((fn, tag = null) => {
    commit((l) => {
      const before = l.elements || [];
      const after = fn(before);
      return after === before ? l : { ...l, elements: after };
    }, tag);
  }, [commit]);

  const stepHistory = useCallback((which) => {
    const s = styleRef.current;
    const cur = layoutsRef.current[s];
    if (!cur) return;
    const step = which === "undo"
      ? G.undoHistory(historyRef.current[s], cur)
      : G.redoHistory(historyRef.current[s], cur);
    if (!step) return;
    historyRef.current[s] = step.history;
    writeLayouts({ ...layoutsRef.current, [s]: step.layout });
    const alive = new Set((step.layout.elements || []).map((e) => e.key));
    setSelection(selectionRef.current.filter((k) => alive.has(k)));
    setDirty(s, true);
    bump();
  }, [writeLayouts, setSelection, setDirty]);
  const hist = historyRef.current[style];
  const canUndo = !!hist?.past?.length;
  const canRedo = !!hist?.future?.length;

  // ---- selection actions -------------------------------------------
  const currentElements = () => layoutsRef.current[styleRef.current]?.elements || [];
  // Locked elements can be selected (from the layers list) but never
  // moved, aligned or deleted.
  const unlockedSelection = useCallback(() => {
    const locked = new Set(currentElements().filter(isLocked).map((e) => e.key));
    return selectionRef.current.filter((k) => !locked.has(k));
  }, []);

  const deleteSelection = useCallback(() => {
    const keys = unlockedSelection();
    if (!keys.length) {
      if (selectionRef.current.length) toast.message("Unlock the element to remove it");
      return;
    }
    commitElements((list) => G.removeElements(list, keys));
    setSelection(selectionRef.current.filter((k) => !keys.includes(k)));
  }, [commitElements, setSelection, unlockedSelection]);

  const addCopies = useCallback((sources) => {
    if (!sources.length) return;
    const result = G.addCopies(currentElements(), sources);
    if (result.added.length) {
      commitElements(() => result.elements);
      setSelection(result.added);
    }
    if (result.skipped.length) {
      toast.message(
        `${result.skipped.map(labelFor).join(", ")} can only appear once per style — not copied`,
      );
    }
  }, [commitElements, setSelection]);

  const duplicateSelection = useCallback(() => {
    const set = new Set(selectionRef.current);
    addCopies(currentElements().filter((e) => set.has(e.key)));
  }, [addCopies]);

  const copySelection = useCallback(() => {
    const set = new Set(selectionRef.current);
    const picked = currentElements().filter((e) => set.has(e.key));
    if (!picked.length) return false;
    clipboardRef.current = JSON.parse(JSON.stringify(picked));
    try {
      navigator.clipboard?.writeText?.(CLIPBOARD_MARKER)?.catch?.(() => {});
    } catch {
      /* clipboard blocked — the in-app copy still works */
    }
    toast.message(`${picked.length} element${picked.length === 1 ? "" : "s"} copied`);
    return true;
  }, []);

  const pasteElements = useCallback(() => {
    addCopies(clipboardRef.current);
  }, [addCopies]);

  const nudge = useCallback((dxPx, dyPx) => {
    const keys = unlockedSelection();
    if (!keys.length) return;
    commitElements((list) => G.nudgeElements(list, keys, dxPx, dyPx), "nudge");
  }, [commitElements, unlockedSelection]);

  const reorder = useCallback((direction) => {
    const keys = selectionRef.current;
    if (!keys.length) return;
    commitElements((list) => G.reorderElements(list, keys, direction));
  }, [commitElements]);

  const align = useCallback((mode) => {
    const keys = unlockedSelection();
    if (!keys.length) return;
    commitElements((list) => G.alignElements(list, keys, mode));
  }, [commitElements, unlockedSelection]);

  const distribute = useCallback((axis) => {
    const keys = unlockedSelection();
    if (keys.length < 3) return;
    commitElements((list) => G.distributeElements(list, keys, axis));
  }, [commitElements, unlockedSelection]);

  const selectAll = useCallback(() => {
    setSelection(currentElements().filter((e) => !isLocked(e)).map((e) => e.key));
  }, [setSelection]);

  const patchElement = useCallback((key, patch) => {
    commitElements(
      (list) => list.map((e) => (e.key === key ? { ...e, ...patch } : e)),
      `prop:${key}:${Object.keys(patch).sort().join(",")}`,
    );
  }, [commitElements]);

  const addElement = useCallback((kind, extra = null) => {
    const list = currentElements();
    const single = SINGLE_KEYS.includes(kind);
    if (single && list.some((e) => e.key === kind)) return null;
    const key = single ? kind : G.uniqueKey(list, kind);
    const el = { ...makeDefaultElement(kind, key), ...(extra || {}) };
    commitElements((cur) => [...cur, el]);
    setSelection([key]);
    return key;
  }, [commitElements, setSelection]);

  // Called by the artboard when a drag or resize ends.
  const commitRects = useCallback((rects) => {
    const keys = Object.keys(rects || {});
    if (!keys.length) return;
    commitElements((list) => {
      let changed = false;
      const next = list.map((e) => {
        const r = rects[e.key];
        if (!r) return e;
        if (r.x === e.x && r.y === e.y && r.w === e.w && r.h === e.h) return e;
        changed = true;
        return { ...e, x: r.x, y: r.y, w: r.w, h: r.h };
      });
      return changed ? next : list;
    });
  }, [commitElements]);

  // ---- images dropped in from the clipboard ------------------------
  const pasteImageMutation = useMutation({
    mutationFn: async (file) => {
      checkPastedSize(file);
      const usable = await coerceImageType(file, ASSET_ACCEPT);
      const uploaded = await api.uploadAdAsset(usable);
      const size = await naturalSize(fileUrl(uploaded.path));
      return { path: uploaded.path, size };
    },
    onSuccess: ({ path, size }) => {
      const base = makeDefaultElement("image", "image");
      const rect = size ? G.fitToAspect(G.rectOf(base), size.w, size.h) : G.rectOf(base);
      addElement("image", { ...rect, src: path });
      toast.success("Image pasted onto the artboard");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || e?.message || "Could not paste that image"),
  });
  const pasteImageRef = useRef(null);
  pasteImageRef.current = (file) => pasteImageMutation.mutate(file);
  const pasteElementsRef = useRef(pasteElements);
  pasteElementsRef.current = pasteElements;
  useEffect(() => pushPasteTarget((event) => {
    if (modalOpenRef.current || isEditableTarget(event.target)) return;
    const file = imageFileFromClipboardEvent(event);
    if (file) {
      event.preventDefault();
      pasteImageRef.current(file);
      return;
    }
    if (clipboardRef.current.length) {
      event.preventDefault();
      pasteElementsRef.current();
    }
  }), []);

  // ---- save / reset / copy to another style ------------------------
  const saveMutation = useMutation({
    mutationFn: async () => {
      const s = styleRef.current;
      const snapshot = layoutsRef.current[s];
      await api.patchAdStyle(ad.id, s, snapshot);
      return { s, snapshot };
    },
    onSuccess: ({ s, snapshot }) => {
      toast.success(`Layout saved for ${AD_STYLES.find((x) => x.key === s)?.label || s}`);
      setLocalSaved((prev) => ({ ...prev, [s]: true }));
      // Only "clean" if nothing changed while the save was in flight.
      if (layoutsRef.current[s] === snapshot) setDirty(s, false);
      qc.invalidateQueries({ queryKey: ["ads"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const save = useCallback(() => {
    if (!layoutsRef.current[styleRef.current] || saveMutation.isPending) return;
    saveMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveMutation.isPending]);

  const resetMutation = useMutation({
    mutationFn: () => api.getAdStyleDefault(styleRef.current),
    onSuccess: (dl) => {
      // Through `commit`, so a reset can be undone like any other edit.
      commit(() => dl);
      setSelection([]);
      toast.success("Reset to default — Save to keep it, or Undo");
    },
  });

  // Duplicate the CURRENT style's layout onto another style. Persists
  // it to the target immediately so the change survives a page reload
  // without needing a second Save click.
  const duplicateMutation = useMutation({
    mutationFn: async (targetKey) => {
      const clone = JSON.parse(JSON.stringify(layoutsRef.current[styleRef.current]));
      await api.patchAdStyle(ad.id, targetKey, clone);
      return { targetKey, clone };
    },
    onSuccess: ({ targetKey, clone }) => {
      writeLayouts({ ...layoutsRef.current, [targetKey]: clone });
      historyRef.current[targetKey] = G.emptyHistory();
      setDirty(targetKey, false);
      setLocalSaved((prev) => ({ ...prev, [targetKey]: true }));
      qc.invalidateQueries({ queryKey: ["ads"] });
      const label = AD_STYLES.find((s) => s.key === targetKey)?.label || targetKey;
      toast.success(`Layout copied to ${label}`);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Copy failed"),
  });
  const [dupOpen, setDupOpen] = useState(false);

  // Closing with unsaved work asks first — a stray click on the dark
  // backdrop used to throw the whole session away.
  const requestClose = useCallback(() => {
    const unsaved = AD_STYLES.filter((s) => dirtyRef.current[s.key]).map((s) => s.label);
    if (
      unsaved.length &&
      !window.confirm(
        `You have unsaved layout changes in: ${unsaved.join(", ")}.\n\nClose without saving?`,
      )
    ) {
      return;
    }
    onClose();
  }, [onClose]);
  const backdropDownRef = useRef(false);

  // ---- keyboard ----------------------------------------------------
  const keyActions = useRef({});
  keyActions.current = {
    save, deleteSelection, duplicateSelection, copySelection, nudge,
    reorder, selectAll, stepHistory, setSelection,
  };
  useEffect(() => {
    const onKey = (e) => {
      if (modalOpenRef.current) return;
      const a = keyActions.current;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key;
      const lower = k.length === 1 ? k.toLowerCase() : k;
      if (mod && lower === "s") {
        e.preventDefault();
        a.save();
        return;
      }
      const t = e.target;
      const tag = String(t?.tagName || "").toUpperCase();
      const inputType = String(t?.type || "").toLowerCase();
      const nonText = ["range", "checkbox", "radio", "color", "button", "file"];
      const typing =
        tag === "TEXTAREA" || t?.isContentEditable === true ||
        (tag === "INPUT" && !nonText.includes(inputType));
      if (typing) return;
      // Sliders and dropdowns keep their own arrow keys.
      const ownsArrows = tag === "SELECT" || (tag === "INPUT" && inputType === "range");

      if (mod && lower === "z") {
        e.preventDefault();
        a.stepHistory(e.shiftKey ? "redo" : "undo");
      } else if (mod && lower === "y") {
        e.preventDefault();
        a.stepHistory("redo");
      } else if (mod && lower === "a") {
        e.preventDefault();
        a.selectAll();
      } else if (mod && lower === "d") {
        e.preventDefault();
        a.duplicateSelection();
      } else if (mod && lower === "c") {
        // Leave a real text selection (e.g. in a toast) to the browser.
        if (String(window.getSelection?.() || "")) return;
        if (a.copySelection()) e.preventDefault();
      } else if (mod && lower === "x") {
        if (a.copySelection()) {
          e.preventDefault();
          a.deleteSelection();
        }
      } else if (mod && (k === "]" || k === "[")) {
        e.preventDefault();
        if (k === "]") a.reorder(e.shiftKey ? "front" : "forward");
        else a.reorder(e.shiftKey ? "back" : "backward");
      } else if (k === "Delete" || k === "Backspace") {
        if (!selectionRef.current.length) return;
        e.preventDefault();
        a.deleteSelection();
      } else if (k === "Escape") {
        a.setSelection([]);
      } else if (!mod && !ownsArrows && k.startsWith("Arrow")) {
        if (!selectionRef.current.length) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        if (k === "ArrowLeft") a.nudge(-step, 0);
        else if (k === "ArrowRight") a.nudge(step, 0);
        else if (k === "ArrowUp") a.nudge(0, -step);
        else if (k === "ArrowDown") a.nudge(0, step);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const switchStyle = (key) => {
    setStyle(key);
    setSelection([]);
  };
  const anyDirty = Object.values(dirty).some(Boolean);

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 bg-black/50 flex items-stretch"
      onMouseDown={(e) => { backdropDownRef.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        // Close only on a click that both started and ended on the
        // backdrop — not on a drag that happened to end out here.
        if (e.target === e.currentTarget && backdropDownRef.current) requestClose();
      }}
    >
      <div
        className="m-4 flex-1 bg-white border border-[var(--border)] shadow-xl flex flex-col overflow-hidden"
        data-testid="ad-style-editor"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">Sponsor ads · Style editor</div>
            <h2 className="font-serif-display text-xl">{ad.name}</h2>
          </div>
          <div className="flex items-center gap-3">
            {anyDirty && (
              <span
                data-testid="ad-editor-unsaved"
                className="text-[10px] font-mono-display text-amber-800 bg-amber-50 border border-amber-300 px-1.5 py-0.5"
              >
                UNSAVED CHANGES
              </span>
            )}
            <button
              onClick={requestClose}
              data-testid="ad-editor-close-btn"
              className="p-2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex overflow-hidden">
          {/* Left rail — style tabs */}
          <aside className="w-52 border-r border-[var(--border)] bg-[var(--secondary)] overflow-y-auto shrink-0">
            <ul className="py-2">
              {AD_STYLES.map((s) => (
                <li key={s.key}>
                  <button
                    data-testid={`ad-style-tab-${s.key}`}
                    onClick={() => switchStyle(s.key)}
                    className={
                      "w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 " +
                      (style === s.key
                        ? "bg-white text-[var(--ink)] border-l-2 border-[var(--accent)] font-medium"
                        : "text-[var(--ink-muted)] hover:bg-white hover:text-[var(--ink)]")
                    }
                  >
                    <span>{s.label}</span>
                    {dirty[s.key] ? (
                      <span
                        title="Unsaved changes"
                        className="ml-auto text-[9px] font-mono-display bg-amber-100 text-amber-800 border border-amber-300 px-1 py-0.5"
                      >
                        EDITED
                      </span>
                    ) : ad.styles?.[s.key] || localSaved[s.key] ? (
                      <span className="ml-auto text-[9px] font-mono-display bg-emerald-100 text-emerald-700 border border-emerald-300 px-1 py-0.5">
                        SAVED
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Middle — toolbar + artboard */}
          <main className="flex-1 flex flex-col overflow-hidden bg-neutral-100 min-w-0">
            <div className="border-b border-[var(--border)] bg-white px-4 py-2 flex flex-wrap items-center gap-2">
              <button
                onClick={() => stepHistory("undo")}
                disabled={!canUndo}
                data-testid="ad-editor-undo"
                title="Undo (Cmd/Ctrl+Z)"
                className={TOOL_BTN}
              >
                <Undo2 className="w-3 h-3" /> Undo
              </button>
              <button
                onClick={() => stepHistory("redo")}
                disabled={!canRedo}
                data-testid="ad-editor-redo"
                title="Redo (Shift+Cmd/Ctrl+Z)"
                className={TOOL_BTN}
              >
                <Redo2 className="w-3 h-3" /> Redo
              </button>
              <span className="w-px h-5 bg-[var(--border)] mx-1" />
              <button
                onClick={() => setShowGrid((v) => !v)}
                data-testid="ad-editor-grid-toggle"
                title="Show a faint 50px grid over the artboard"
                className={showGrid ? TOOL_BTN_ON : TOOL_BTN}
              >
                Grid
              </button>
              <button
                onClick={() => setSnapToGrid((v) => !v)}
                data-testid="ad-editor-snap-toggle"
                title="Snap moves and resizes to the 50px grid"
                className={snapToGrid ? TOOL_BTN_ON : TOOL_BTN}
              >
                Snap 50px
              </button>
              <button
                onClick={() => setSmartGuides((v) => !v)}
                data-testid="ad-editor-guides-toggle"
                title="While moving, line elements up with each other and with the artboard's edges and centre"
                className={smartGuides ? TOOL_BTN_ON : TOOL_BTN}
              >
                Guides
              </button>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setBannerOpen(true)}
                  disabled={!layout}
                  data-testid="ad-editor-banner-btn"
                  title="Build a 1300×200 banner from text and photos, or from a finished ad"
                  className={TOOL_BTN}
                >
                  <Sparkles className="w-3 h-3" /> Create banner
                </button>
                <div className="relative">
                  <button
                    data-testid="ad-editor-duplicate"
                    onClick={() => setDupOpen((v) => !v)}
                    disabled={!layout || duplicateMutation.isPending}
                    className={TOOL_BTN}
                  >
                    {duplicateMutation.isPending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                    Duplicate to…
                  </button>
                  {dupOpen && (
                    <div
                      data-testid="ad-editor-duplicate-menu"
                      className="absolute right-0 top-full mt-1 z-30 bg-white border border-[var(--border)] shadow-md min-w-[220px]"
                      onMouseLeave={() => setDupOpen(false)}
                    >
                      {AD_STYLES.filter((s) => s.key !== style).map((s) => (
                        <button
                          key={s.key}
                          data-testid={`ad-editor-duplicate-to-${s.key}`}
                          onClick={() => {
                            setDupOpen(false);
                            duplicateMutation.mutate(s.key);
                          }}
                          className="w-full text-left text-xs px-3 py-2 hover:bg-[var(--secondary)] flex items-center gap-2"
                        >
                          <ChevronRight className="w-3 h-3 text-[var(--ink-muted)]" />
                          <span>{s.label}</span>
                          {(ad.styles?.[s.key] || localSaved[s.key] || dirty[s.key]) && (
                            <span className="ml-auto text-[9px] font-mono-display bg-amber-100 text-amber-800 border border-amber-300 px-1 py-0.5">
                              WILL OVERWRITE
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setFontsModalOpen(true)}
                  data-testid="ad-editor-fonts-btn"
                  className={TOOL_BTN}
                >
                  <Settings className="w-3 h-3" />
                  Fonts
                </button>
                <button
                  onClick={() => resetMutation.mutate()}
                  disabled={resetMutation.isPending}
                  data-testid="ad-editor-reset"
                  className={TOOL_BTN}
                >
                  {resetMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3 h-3" />
                  )}
                  Reset
                </button>
                <button
                  onClick={save}
                  disabled={saveMutation.isPending || !layout}
                  data-testid="ad-editor-save"
                  title="Save this style (Cmd/Ctrl+S)"
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-40"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                  Save {style}{dirty[style] ? " •" : ""}
                </button>
              </div>
            </div>

            {/* Artboard */}
            <div className="flex-1 overflow-auto p-6">
              {layout ? (
                <EditorCanvas
                  ad={adDraft}
                  adRev={adRev}
                  layout={layout}
                  style={style}
                  snapToGrid={snapToGrid}
                  showGrid={showGrid}
                  smartGuides={smartGuides}
                  fontFamilies={fontFamilies}
                  selection={selection}
                  onSelect={setSelection}
                  onCommitRects={commitRects}
                />
              ) : (
                <div className="text-[var(--ink-muted)] flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading layout…
                </div>
              )}
            </div>
          </main>

          {/* Right rail — layers + inspector */}
          <aside className="w-72 border-l border-[var(--border)] bg-white overflow-y-auto shrink-0">
            <div className="px-4 py-3 border-b border-[var(--border)]">
              <div className="overline mb-2">Layers · top first</div>
              <ul className="space-y-1">
                {[...elements].reverse().map((el) => {
                  const kind = G.elementKind(el.key);
                  const on = selection.includes(el.key);
                  return (
                    <li key={el.key} className="flex items-center gap-1">
                      <button
                        data-testid={`ad-editor-element-${el.key}`}
                        onClick={(e) => {
                          if (e.shiftKey || e.metaKey || e.ctrlKey) {
                            setSelection(
                              on
                                ? selection.filter((k) => k !== el.key)
                                : [...selection, el.key],
                            );
                          } else {
                            setSelection([el.key]);
                          }
                        }}
                        className={
                          "flex-1 min-w-0 text-left text-xs px-2 py-1.5 border flex items-center gap-2 " +
                          (on
                            ? "border-[var(--accent)] bg-[var(--secondary)]"
                            : "border-transparent hover:bg-[var(--secondary)]")
                        }
                      >
                        {kind === "image" || kind === "barImage" || kind === "logo" ? (
                          <ImageIcon className="w-3 h-3 shrink-0" />
                        ) : kind === "bar" ? (
                          <Square className="w-3 h-3 shrink-0" />
                        ) : (
                          <Type className="w-3 h-3 shrink-0" />
                        )}
                        <span className="truncate">
                          {labelFor(el.key)}
                          {kind === "customText" && el.text ? (
                            <span className="text-[var(--ink-muted)]"> · {el.text}</span>
                          ) : null}
                        </span>
                      </button>
                      <button
                        data-testid={`ad-editor-lock-${el.key}`}
                        title={
                          isLocked(el)
                            ? "Locked — click to unlock"
                            : "Lock: keeps it from being moved or picked up on the artboard"
                        }
                        onClick={() => patchElement(el.key, { locked: !isLocked(el) })}
                        className={
                          "p-1 " +
                          (isLocked(el)
                            ? "text-[var(--ink)]"
                            : "text-[var(--ink-muted)] opacity-40 hover:opacity-100")
                        }
                      >
                        {isLocked(el) ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="overline mt-3 mb-1.5">Add</div>
              <div className="flex flex-wrap gap-1">
                {["logo", "title", "subhead", "phone", "website", "email"].map((k) => (
                  <button
                    key={k}
                    data-testid={`ad-editor-add-${k}`}
                    disabled={!layout || elements.some((e) => e.key === k)}
                    onClick={() => addElement(k)}
                    className={MINI_BTN}
                  >
                    + {k}
                  </button>
                ))}
                {/* Sponsor bar — one per style from here; duplicate it
                    on the artboard for more. */}
                <button
                  data-testid="ad-editor-add-sponsor_bar"
                  disabled={!layout || elements.some((e) => e.key === "sponsor_bar")}
                  onClick={() => addElement("sponsor_bar")}
                  className={MINI_BTN}
                >
                  + sponsor bar
                </button>
                {/* As many of these as the editor wants. */}
                <button
                  data-testid="ad-editor-add-image"
                  disabled={!layout}
                  onClick={() => addElement("image")}
                  className={MINI_BTN}
                >
                  + image
                </button>
                <button
                  data-testid="ad-editor-add-sponsor_bar_image"
                  disabled={!layout}
                  onClick={() => addElement("sponsor_bar_image")}
                  className={MINI_BTN}
                >
                  + sponsor bar image
                </button>
                <button
                  data-testid="ad-editor-add-text"
                  disabled={!layout}
                  onClick={() => addElement("text")}
                  className={MINI_BTN}
                >
                  + text
                </button>
              </div>
            </div>

            {selectedEls.length > 1 && (
              <MultiInspector
                count={selectedEls.length}
                onAlign={align}
                onDistribute={distribute}
                onDuplicate={duplicateSelection}
                onRemove={deleteSelection}
                onReorder={reorder}
              />
            )}
            {selectedEl && (
              <ElementInspector
                key={`${style}:${selectedEl.key}`}
                el={selectedEl}
                ad={adDraft}
                fontFamilies={fontFamilies}
                onAlign={align}
                onClone={duplicateSelection}
                onReorder={reorder}
                onAdChange={onAdChange}
                onChange={(patch) => patchElement(selectedEl.key, patch)}
                onRemove={deleteSelection}
              />
            )}
            {selectedEls.length === 0 && <ShortcutHelp />}
          </aside>
        </div>
      </div>
      {fontsModalOpen && (
        <FontsModal onClose={() => setFontsModalOpen(false)} />
      )}
      {bannerOpen && (
        <AdBannerDialog
          ad={adDraft}
          onClose={() => setBannerOpen(false)}
          onInsert={(path) => {
            const el = G.bannerElement(currentElements(), path);
            commitElements((list) => [...list, el]);
            setSelection([el.key]);
            setBannerOpen(false);
            toast.success("Banner added to the artboard — drag it where you want it");
          }}
        />
      )}
    </div>
  );
}

function ShortcutHelp() {
  const rows = [
    ["Drag on empty space", "select several"],
    ["Alt/Option + drag", "select through an element"],
    ["Shift + click", "add to / remove from selection"],
    ["Delete / Backspace", "remove"],
    ["Arrow keys", "move 1px (Shift: 10px)"],
    ["Shift while resizing", "switch keep-shape on/off"],
    ["Cmd/Ctrl + Z / Shift+Z", "undo / redo"],
    ["Cmd/Ctrl + C / V / D", "copy / paste / duplicate"],
    ["Cmd/Ctrl + V (a picture)", "paste it as a new image"],
    ["Cmd/Ctrl + A", "select all"],
    ["Cmd/Ctrl + ] / [", "bring forward / send back"],
    ["Cmd/Ctrl + S", "save this style"],
    ["Esc", "deselect / cancel a drag"],
  ];
  return (
    <div className="p-4" data-testid="ad-editor-shortcuts">
      <div className="overline mb-2">Nothing selected</div>
      <p className="text-[11px] text-[var(--ink-muted)] mb-3">
        Click an element on the artboard, or pick it in Layers.
      </p>
      <dl className="space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2 text-[10px]">
            <dt className="font-mono-display w-[46%] shrink-0">{k}</dt>
            <dd className="text-[var(--ink-muted)]">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ---------- Artboard -------------------------------------------------------
// The server's render (1300×800) is the background; each element is an
// absolutely-positioned box in percent units on top of it.
const HANDLE_STYLE = {
  nw: { left: -6, top: -6, cursor: "nwse-resize" },
  n:  { left: "calc(50% - 6px)", top: -6, cursor: "ns-resize" },
  ne: { right: -6, top: -6, cursor: "nesw-resize" },
  e:  { right: -6, top: "calc(50% - 6px)", cursor: "ew-resize" },
  se: { right: -6, bottom: -6, cursor: "nwse-resize" },
  s:  { left: "calc(50% - 6px)", bottom: -6, cursor: "ns-resize" },
  sw: { left: -6, bottom: -6, cursor: "nesw-resize" },
  w:  { left: -6, top: "calc(50% - 6px)", cursor: "ew-resize" },
};

function pctStyle(r) {
  return { left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%` };
}

// "260 × 160 px · x 520, y 320" — used by React AND by the drag loop, so
// the two can never disagree about what the label says.
function sizeText(r) {
  return (
    `${Math.round(G.pctToPxX(r.w))} × ${Math.round(G.pctToPxY(r.h))} px` +
    ` · x ${Math.round(G.pctToPxX(r.x))}, y ${Math.round(G.pctToPxY(r.y))}`
  );
}

function EditorCanvas({
  ad, adRev, layout, style, snapToGrid, showGrid, smartGuides, fontFamilies,
  selection, onSelect, onCommitRects,
}) {
  const canvasRef = useRef(null);
  const boxRefs = useRef({});
  const frameRef = useRef(null);
  const hudRef = useRef(null);
  const marqueeRef = useRef(null);
  const guideXRef = useRef(null);
  const guideYRef = useRef(null);
  // Rects of the elements being dragged right now, by key. Render reads
  // from here first, so a re-render mid-drag (a preview arriving) can't
  // snap an element back to where it started.
  const liveRef = useRef({});
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const [canvasH, setCanvasH] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  // Elements currently drawn by the browser instead of the server.
  const [ghostKeys, setGhostKeys] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const seqRef = useRef(0);
  const abortRef = useRef(null);
  const immediateRef = useRef(false);
  const urlRef = useRef(null);
  const elements = layout.elements || [];

  // Track the artboard's on-screen height (ghost text is sized from it).
  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return undefined;
    const measure = () => setCanvasH(node.clientHeight || 0);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // One preview at a time: a newer request aborts the older one, and
  // `seq` makes sure a slow answer can never replace a newer picture.
  // `transient` = the mid-drag render with the dragged elements left
  // out; it must not clear the ghosts or show the "Rendering…" chip.
  const fetchPreview = useCallback(async (layoutToRender, transient) => {
    const seq = ++seqRef.current;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (!transient) setPreviewLoading(true);
    try {
      const blob = await api.previewAdStyle(ad.id, style, layoutToRender, ctrl.signal);
      if (seq !== seqRef.current) return;
      const url = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPreviewUrl(url);
      setPreviewFailed(false);
      if (!transient) setGhostKeys([]);
    } catch (_e) {
      if (seq !== seqRef.current) return;   // aborted or overtaken
      // Non-fatal — the layout can still be saved; the red frame says
      // the picture is out of date.
      setPreviewFailed(true);
      if (!transient) setGhostKeys([]);
    } finally {
      if (seq === seqRef.current) setPreviewLoading(false);
    }
  }, [ad.id, style]);

  useEffect(() => {
    const delay = immediateRef.current ? 0 : 140;
    immediateRef.current = false;
    const t = setTimeout(() => fetchPreview(layout, false), delay);
    return () => clearTimeout(t);
  }, [layout, adRev, refreshTick, fetchPreview]);

  useEffect(() => () => {
    abortRef.current?.abort();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // ---- painting straight to the DOM during a drag -------------------
  const paintBox = (key, r) => {
    const node = boxRefs.current[key];
    if (!node) return;
    node.style.left = `${r.x}%`;
    node.style.top = `${r.y}%`;
    node.style.width = `${r.w}%`;
    node.style.height = `${r.h}%`;
  };
  const paintFrame = (r) => {
    const node = frameRef.current;
    if (!node || !r) return;
    node.style.left = `${r.x}%`;
    node.style.top = `${r.y}%`;
    node.style.width = `${r.w}%`;
    node.style.height = `${r.h}%`;
    if (hudRef.current) hudRef.current.textContent = sizeText(r);
  };
  const paintGuide = (ref, axis, value) => {
    const node = ref.current;
    if (!node) return;
    if (value === null || value === undefined) {
      node.style.display = "none";
      return;
    }
    node.style.display = "block";
    if (axis === "x") node.style.left = `${value}%`;
    else node.style.top = `${value}%`;
  };
  const markHits = (keys) => {
    const set = new Set(keys);
    for (const [key, node] of Object.entries(boxRefs.current)) {
      if (node) node.style.boxShadow = set.has(key) ? "inset 0 0 0 2px #1877F2" : "";
    }
  };
  const blurActive = () => {
    const a = document.activeElement;
    if (a && a !== document.body && typeof a.blur === "function") a.blur();
  };

  const begin = (e, d) => {
    const node = canvasRef.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    d.pointerId = e.pointerId;
    d.startX = e.clientX;
    d.startY = e.clientY;
    d.left = r.left + node.clientLeft;
    d.top = r.top + node.clientTop;
    d.width = node.clientWidth || 1;
    d.height = node.clientHeight || 1;
    d.pending = true;         // becomes a drag after 3px of travel
    d.last = null;
    if (d.keys) {
      const set = new Set(d.keys);
      d.starts = {};
      for (const el of elements) {
        if (set.has(el.key)) d.starts[el.key] = G.rectOf(el);
      }
      d.box0 = G.boundsOf(Object.values(d.starts));
      d.others = elements.filter((el) => !set.has(el.key)).map(G.rectOf);
    }
    dragRef.current = d;
    try {
      node.setPointerCapture(e.pointerId);
    } catch {
      /* older browsers — window-less drags still work inside the artboard */
    }
  };

  const startElementDrag = (e, el) => {
    if (e.button !== 0) return;
    // Alt/Option-drag falls through to the artboard: a marquee that
    // starts on top of an element. (Not Cmd/Ctrl — for admins that key
    // is the app-wide "prompt-only" switch.)
    if (e.altKey) return;
    e.stopPropagation();
    blurActive();
    const has = selection.includes(el.key);
    if (e.shiftKey) {
      onSelect(has ? selection.filter((k) => k !== el.key) : [...selection, el.key]);
      return;
    }
    // Pressing inside something that is already selected drags the
    // selection, even when another element lies on top at that spot —
    // otherwise an element picked in Layers but covered on the artboard
    // could never be moved with the pointer. A plain click (no drag)
    // still selects the element that was actually clicked.
    const lockedKeys = new Set(elements.filter(isLocked).map((x) => x.key));
    let insideSelection = has;
    if (!has && selection.length) {
      const node = canvasRef.current;
      const cr = node.getBoundingClientRect();
      const px = ((e.clientX - cr.left - node.clientLeft) / (node.clientWidth || 1)) * 100;
      const py = ((e.clientY - cr.top - node.clientTop) / (node.clientHeight || 1)) * 100;
      insideSelection = elements.some((x) =>
        selection.includes(x.key) && !lockedKeys.has(x.key) &&
        px >= x.x && px <= x.x + x.w && py >= x.y && py <= x.y + x.h);
    }
    const next = insideSelection ? selection : [el.key];
    if (!insideSelection) onSelect(next);
    begin(e, {
      mode: "move",
      keys: next.filter((k) => !lockedKeys.has(k)),
      clickedKey: el.key,
      collapseOnClick: insideSelection && !(has && next.length === 1),
    });
  };

  const startResize = (e, handle, el) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    blurActive();
    begin(e, { mode: "resize", keys: [el.key], handle, el });
  };

  const startMarquee = (e) => {
    if (e.button !== 0) return;
    blurActive();
    begin(e, { mode: "marquee", additive: e.shiftKey, base: selection });
  };

  const applyDrag = () => {
    rafRef.current = 0;
    const d = dragRef.current;
    if (!d || d.pending || !d.last) return;
    const dx = ((d.last.x - d.startX) / d.width) * 100;
    const dy = ((d.last.y - d.startY) / d.height) * 100;

    if (d.mode === "marquee") {
      const toPct = (v, origin, size) => Math.max(0, Math.min(100, ((v - origin) / size) * 100));
      const rect = G.rectFromPoints(
        toPct(d.startX, d.left, d.width), toPct(d.startY, d.top, d.height),
        toPct(d.last.x, d.left, d.width), toPct(d.last.y, d.top, d.height),
      );
      d.rect = rect;
      const node = marqueeRef.current;
      if (node) {
        node.style.display = "block";
        node.style.left = `${rect.x}%`;
        node.style.top = `${rect.y}%`;
        node.style.width = `${rect.w}%`;
        node.style.height = `${rect.h}%`;
      }
      d.hits = G.marqueeHits(elements.filter((el) => !isLocked(el)), rect);
      markHits(d.hits);
      return;
    }

    if (d.mode === "move") {
      if (!d.box0) return;
      let mdx = dx;
      let mdy = dy;
      let guideX = null;
      let guideY = null;
      if (snapToGrid) {
        mdx = G.snapToGridPct(d.box0.x + dx, "x") - d.box0.x;
        mdy = G.snapToGridPct(d.box0.y + dy, "y") - d.box0.y;
      } else if (smartGuides) {
        const cdx = Math.max(-d.box0.x, Math.min(100 - d.box0.x - d.box0.w, dx));
        const cdy = Math.max(-d.box0.y, Math.min(100 - d.box0.y - d.box0.h, dy));
        // 6 screen pixels of pull, whatever size the artboard is shown at.
        const s = G.snapMove(
          { x: d.box0.x + cdx, y: d.box0.y + cdy, w: d.box0.w, h: d.box0.h },
          d.others, (6 / d.width) * 100, (6 / d.height) * 100,
        );
        mdx = cdx + s.dx;
        mdy = cdy + s.dy;
        guideX = s.guideX;
        guideY = s.guideY;
      }
      const moved = G.moveRects(d.starts, mdx, mdy);
      const live = {};
      for (const key of Object.keys(d.starts)) {
        live[key] = { ...d.starts[key], ...moved[key] };
        paintBox(key, live[key]);
      }
      liveRef.current = live;
      paintFrame(G.boundsOf(Object.values(live)));
      paintGuide(guideXRef, "x", guideX);
      paintGuide(guideYRef, "y", guideY);
      return;
    }

    // resize
    const key = d.keys[0];
    const start = d.starts[key];
    if (!start) return;
    // Shift flips the element's own "Keep shape" setting for this drag.
    const keep = G.keepsShape(d.el) !== !!d.last.shift;
    let r = G.resizeRect(start, d.handle, dx, dy, { keepRatio: keep });
    if (snapToGrid && !keep) r = G.snapResizeToGrid(r, d.handle);
    liveRef.current = { [key]: r };
    paintBox(key, r);
    paintFrame(r);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    if (d.pending) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 3) return;
      d.pending = false;
      if (d.mode !== "marquee" && d.keys.length) {
        // Draw the dragged elements in the browser from here on, and
        // ask the server for the picture without them.
        const set = new Set(d.keys);
        setGhostKeys(d.keys);
        fetchPreview(
          { ...layout, elements: elements.filter((el) => !set.has(el.key)) }, true,
        );
      }
    }
    d.last = { x: e.clientX, y: e.clientY, shift: e.shiftKey };
    if (!rafRef.current) rafRef.current = requestAnimationFrame(applyDrag);
  };

  const finishDrag = (e, cancelled) => {
    const d = dragRef.current;
    if (!d || (e && e.pointerId !== d.pointerId)) return;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    // Apply the very last pointer position before letting go.
    if (!cancelled && !d.pending && d.last) applyDrag();
    dragRef.current = null;
    try {
      canvasRef.current?.releasePointerCapture(d.pointerId);
    } catch {
      /* already released */
    }
    if (marqueeRef.current) marqueeRef.current.style.display = "none";
    paintGuide(guideXRef, "x", null);
    paintGuide(guideYRef, "y", null);
    markHits([]);

    if (d.pending) {
      // Never moved: it was a click.
      if (d.mode === "marquee") {
        if (!d.additive && !cancelled) onSelect([]);
      } else if (d.mode === "move" && d.collapseOnClick && !cancelled) {
        onSelect([d.clickedKey]);
      }
      return;
    }
    if (d.mode === "marquee") {
      if (cancelled) return;
      const hits = d.hits || [];
      onSelect(d.additive ? Array.from(new Set([...(d.base || []), ...hits])) : hits);
      return;
    }
    const live = liveRef.current;
    liveRef.current = {};
    if (cancelled || !Object.keys(live).length) {
      // Put everything back where it started.
      for (const key of Object.keys(d.starts || {})) paintBox(key, d.starts[key]);
      paintFrame(G.boundsOf(Object.values(d.starts || {})));
    } else {
      onCommitRects(live);
    }
    // Fresh server picture now; it clears the ghosts when it lands.
    immediateRef.current = true;
    setRefreshTick((n) => n + 1);
  };

  const finishDragRef = useRef(finishDrag);
  finishDragRef.current = finishDrag;

  // Esc cancels a drag in progress (and stops there — the editor's own
  // Esc handler would otherwise also clear the selection).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || !dragRef.current) return;
      e.stopImmediatePropagation();
      finishDragRef.current(null, true);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
  const selectedEls = elements.filter((el) => selection.includes(el.key));
  const frameRect = selectedEls.length
    ? G.boundsOf(selectedEls.map((el) => liveRef.current[el.key] || G.rectOf(el)))
    : null;
  const single = selectedEls.length === 1 ? selectedEls[0] : null;

  return (
    <div className="flex flex-col items-center w-full">
      {/* Wrapper: [16px corner] + [top ruler] on top row; [left ruler] + artboard on second row */}
      <div
        className="relative w-full"
        style={{ maxWidth: "min(1116px, 100%)" }}
      >
        {/* Top ruler */}
        <div
          className="absolute left-4 right-0 top-0 h-4 border-b border-[var(--border)]/60 bg-white/70 z-10 overflow-hidden text-[9px] font-mono-display text-[var(--ink-muted)]"
          data-testid="ad-editor-ruler-top"
        >
          {/* 50-px minor ticks (unlabeled, ~half the height of the 100-px ticks) */}
          {Array.from({ length: Math.floor(CANVAS_W / 50) + 1 }).map((_, i) => {
            const px = i * 50;
            if (px % 100 === 0) return null;  // skip major-tick positions
            return (
              <div
                key={`m-${px}`}
                className="absolute top-0 w-px h-1 bg-[var(--ink-muted)]/40"
                style={{ left: `${(px / CANVAS_W) * 100}%` }}
              />
            );
          })}
          {Array.from({ length: Math.floor(CANVAS_W / 100) + 1 }).map((_, i) => {
            const px = i * 100;
            return (
              <div
                key={i}
                className="absolute top-0 bottom-0"
                style={{ left: `${(px / CANVAS_W) * 100}%` }}
              >
                <div className="w-px h-2 bg-[var(--ink-muted)]/60" />
                {i > 0 && <div className="pl-0.5 -mt-0.5">{px}</div>}
              </div>
            );
          })}
        </div>
        {/* Left ruler */}
        <div
          className="absolute left-0 top-4 bottom-0 w-4 border-r border-[var(--border)]/60 bg-white/70 z-10 overflow-hidden text-[9px] font-mono-display text-[var(--ink-muted)]"
          data-testid="ad-editor-ruler-left"
        >
          {/* 50-px minor ticks */}
          {Array.from({ length: Math.floor(CANVAS_H / 50) + 1 }).map((_, i) => {
            const px = i * 50;
            if (px % 100 === 0) return null;
            return (
              <div
                key={`m-${px}`}
                className="absolute left-0 h-px w-1 bg-[var(--ink-muted)]/40"
                style={{ top: `${(px / CANVAS_H) * 100}%` }}
              />
            );
          })}
          {Array.from({ length: Math.floor(CANVAS_H / 100) + 1 }).map((_, i) => {
            const px = i * 100;
            return (
              <div
                key={i}
                className="absolute left-0 right-0"
                style={{ top: `${(px / CANVAS_H) * 100}%` }}
              >
                <div className="h-px w-2 bg-[var(--ink-muted)]/60" />
                {i > 0 && (
                  <div
                    className="absolute right-0.5 top-0.5"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    {px}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Corner */}
        <div className="absolute left-0 top-0 w-4 h-4 border-r border-b border-[var(--border)]/60 bg-white/70 z-10" />
        {/* Artboard, offset by the ruler thickness */}
        <div
          ref={canvasRef}
          data-testid="ad-editor-canvas"
          className={
            "relative bg-white border shadow-sm ml-4 mt-4 touch-none select-none " +
            (previewFailed ? "border-red-500" : "border-[var(--border)]")
          }
          style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}`, cursor: "crosshair" }}
          onPointerDown={startMarquee}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => finishDrag(e, false)}
          onPointerCancel={(e) => finishDrag(e, true)}
        >
        {/* The server's render */}
        {previewUrl && (
          <img
            src={previewUrl}
            alt=""
            draggable={false}
            className="absolute inset-0 w-full h-full pointer-events-none select-none"
            data-testid="ad-editor-preview-img"
          />
        )}
        {previewLoading && (
          <div className="absolute top-2 right-2 z-30 bg-white/90 border border-[var(--border)] px-2 py-1 text-[10px] font-mono-display flex items-center gap-1 pointer-events-none">
            <Loader2 className="w-3 h-3 animate-spin" /> Rendering…
          </div>
        )}
        {previewFailed && !previewLoading && (
          <div className="absolute top-2 right-2 z-30 bg-white/90 border border-red-300 text-red-700 px-2 py-1 text-[10px] font-mono-display pointer-events-none">
            Preview out of date — change anything to retry
          </div>
        )}
        {/* Optional 50px grid overlay. SVG's viewBox is fixed at
            the canvas's logical coordinate space (1300×800) so the
            lines auto-scale exactly with the DOM box — 50px stays
            50px in the underlying render regardless of window size.
            preserveAspectRatio="none" lets the SVG fill the box even
            when the browser rounds the aspect ratio by half a pixel. */}
        {showGrid && (
          <svg
            data-testid="ad-editor-grid-overlay"
            viewBox="0 0 1300 800"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full pointer-events-none"
          >
            <g stroke="rgba(0,0,0,0.08)" strokeWidth="1" vectorEffect="non-scaling-stroke">
              {Array.from({ length: 1300 / 50 + 1 }).map((_, i) => (
                <line key={`v${i}`} x1={i * 50} y1={0} x2={i * 50} y2={800} />
              ))}
              {Array.from({ length: 800 / 50 + 1 }).map((_, i) => (
                <line key={`h${i}`} x1={0} y1={i * 50} x2={1300} y2={i * 50} />
              ))}
            </g>
            <g stroke="rgba(0,0,0,0.18)" strokeWidth="1" vectorEffect="non-scaling-stroke">
              {Array.from({ length: 1300 / 100 + 1 }).map((_, i) => (
                <line key={`vh${i}`} x1={i * 100} y1={0} x2={i * 100} y2={800} />
              ))}
              {Array.from({ length: 800 / 100 + 1 }).map((_, i) => (
                <line key={`hh${i}`} x1={0} y1={i * 100} x2={1300} y2={i * 100} />
              ))}
            </g>
          </svg>
        )}
        {/* One box per element, in layer order (last = on top) */}
        {elements.map((el) => {
          const r = liveRef.current[el.key] || el;
          const on = selection.includes(el.key);
          const ghost = ghostKeys.includes(el.key);
          const kind = G.elementKind(el.key);
          const emptyImage = (kind === "image" || kind === "barImage") && !el.src;
          return (
            <div
              key={el.key}
              ref={(node) => {
                if (node) boxRefs.current[el.key] = node;
                else delete boxRefs.current[el.key];
              }}
              data-testid={`ad-editor-drag-${el.key}`}
              onPointerDown={(e) => startElementDrag(e, el)}
              className={
                "absolute overflow-hidden " +
                (isLocked(el) ? "pointer-events-none " : "cursor-move ") +
                (on
                  ? "outline outline-2 outline-[#1877F2] outline-offset-0"
                  : isLocked(el)
                    ? ""
                    : "outline outline-1 outline-dashed outline-[#1877F2]/40 hover:outline-[#1877F2]")
              }
              style={pctStyle(r)}
            >
              {ghost && (
                <GhostContent el={el} ad={ad} canvasH={canvasH} fontFamilies={fontFamilies} />
              )}
              {!ghost && emptyImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-[10px] font-mono-display text-[var(--ink-muted)] text-center px-1">
                  Empty image — upload one in the panel
                </div>
              )}
            </div>
          );
        })}
        {/* Smart guides */}
        <div
          ref={guideXRef}
          className="absolute top-0 bottom-0 w-px bg-fuchsia-500 pointer-events-none z-20"
          style={{ display: "none" }}
        />
        <div
          ref={guideYRef}
          className="absolute left-0 right-0 h-px bg-fuchsia-500 pointer-events-none z-20"
          style={{ display: "none" }}
        />
        {/* Selection frame: size read-out, and the 8 resize handles when
            exactly one unlocked element is selected */}
        {frameRect && (
          <div
            ref={frameRef}
            className={
              "absolute pointer-events-none z-20 " +
              (single ? "" : "outline outline-1 outline-dashed outline-[#1877F2]")
            }
            style={pctStyle(frameRect)}
            data-testid="ad-editor-selection-frame"
          >
            <div className="absolute -top-4 left-0 text-[9px] font-mono-display bg-[#1877F2] text-white px-1 whitespace-nowrap">
              {single ? `${labelFor(single.key)} · ` : `${selectedEls.length} selected · `}
              <span ref={hudRef}>{sizeText(frameRect)}</span>
              {single && isLocked(single) ? " · locked" : ""}
            </div>
            {single && !isLocked(single) && G.HANDLES.map((h) => (
              <div
                key={h}
                onPointerDown={(e) => startResize(e, h, single)}
                className="absolute w-3 h-3 bg-white border-2 border-[#1877F2] pointer-events-auto"
                style={HANDLE_STYLE[h]}
                data-testid={`ad-editor-resize-${single.key}-${h}`}
              />
            ))}
          </div>
        )}
        {/* Marquee (drag on empty artboard) */}
        <div
          ref={marqueeRef}
          data-testid="ad-editor-marquee"
          className="absolute border border-[#1877F2] bg-[#1877F2]/10 pointer-events-none z-20"
          style={{ display: "none" }}
        />
      </div>
      </div>
      <div className="mt-2 text-[10px] text-[var(--ink-muted)] font-mono-display text-center">
        Artboard {CANVAS_W}×{CANVAS_H} · drag on empty space to select several · Shift-click adds ·
        Delete removes · arrows nudge
      </div>
    </div>
  );
}

// ---------- GhostContent ---------------------------------------------------
// A browser-drawn stand-in for one element, shown only while it is being
// dragged or resized (and for the fraction of a second until the server's
// render catches up). Images and bars are exact; text is an approximation
// of the server's fitted type — close enough to place it.
function hexToRgba(hex, alpha) {
  const s = String(hex || "").replace("#", "");
  const full = s.length === 3 ? s.split("").map((c) => c + c).join("") : s;
  if (full.length !== 6) return `rgba(17,24,39,${alpha})`;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return `rgba(17,24,39,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

function autoInk(hex) {
  const s = String(hex || "#111827").replace("#", "");
  const full = s.length === 3 ? s.split("").map((c) => c + c).join("") : s;
  const n = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return "#ffffff";
  const lum = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return lum < 140 ? "#ffffff" : "#111111";
}

function GhostContent({ el, ad, canvasH, fontFamilies }) {
  const kind = G.elementKind(el.key);
  const opacity = el.opacity == null ? 1 : Math.max(0, Math.min(1, Number(el.opacity) / 100));
  if (kind === "image" || kind === "barImage" || kind === "logo") {
    const src = fileUrl(kind === "logo" ? ad.logo_path : el.src);
    if (!src) {
      return <div className="absolute inset-0 bg-white/60" />;
    }
    const stretch = kind !== "logo" && (el.fit || (kind === "barImage" ? "stretch" : "contain")) === "stretch";
    return (
      <img
        src={src}
        alt=""
        draggable={false}
        className="absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{ objectFit: stretch ? "fill" : "contain", opacity }}
      />
    );
  }
  if (kind === "bar") {
    const mode = el.mode || "solid";
    if (mode === "none") return null;
    const color = el.color || ad.background_color || "#111827";
    const alpha = Math.max(0, Math.min(1, Number(el.alpha ?? 92) / 100));
    const solid = hexToRgba(color, alpha);
    const to = { up: "to top", down: "to bottom", left: "to left", right: "to right" }[el.direction || "up"];
    return (
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity,
          background: mode === "gradient"
            ? `linear-gradient(${to}, ${solid}, ${hexToRgba(color, 0)})`
            : solid,
        }}
      />
    );
  }
  // text
  let text = "";
  if (kind === "customText") text = el.text || "";
  else if (el.key === "website") text = String(ad.website || "").replace(/^https?:\/\//, "");
  else text = ad[el.key] || "";
  const weight = el.font_weight || "regular";
  const stack = (fontFamilies || []).find((f) => f.key === (el.font_family || "sans"))?.stack;
  const align = el.align || "left";
  return (
    <div
      className="absolute inset-0 flex items-center pointer-events-none overflow-hidden"
      style={{
        containerType: "size",
        opacity,
        justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          lineHeight: 1,
          fontFamily: stack,
          fontWeight: weight.startsWith("bold") ? 700 : 400,
          fontStyle: weight.endsWith("italic") ? "italic" : "normal",
          color: el.font_color && el.font_color !== "auto" ? el.font_color : autoInk(ad.background_color),
          // Explicit size is a % of the artboard height; "auto" fills
          // the box, so it tracks the box while it is being resized.
          fontSize: el.font_px != null && canvasH
            ? `${(Number(el.font_px) / 100) * canvasH}px`
            : "90cqh",
        }}
      >
        {text}
      </span>
    </div>
  );
}

// ---------- Inspectors -----------------------------------------------------
const ALIGN_BUTTONS = [
  ["left", "Left"], ["hcenter", "Centre"], ["right", "Right"],
  ["top", "Top"], ["vcenter", "Middle"], ["bottom", "Bottom"],
];

function AlignRow({ onAlign, toArtboard }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
        {toArtboard ? "Align on artboard" : "Align to each other"}
      </div>
      <div className="grid grid-cols-3 gap-1">
        {ALIGN_BUTTONS.map(([mode, label]) => (
          <button
            key={mode}
            data-testid={`ad-editor-align-${mode}`}
            onClick={() => onAlign(mode)}
            className="text-[10px] px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)]"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function OrderButtons({ onReorder, testKey }) {
  const cls = "p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]";
  return (
    <>
      <button onClick={() => onReorder("back")} title="Send to back" className={cls} data-testid={`ad-editor-send-back-${testKey}`}>
        <ArrowDownToLine className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onReorder("backward")} title="Send backward one step (Cmd/Ctrl+[)" className={cls} data-testid={`ad-editor-send-backward-${testKey}`}>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onReorder("forward")} title="Bring forward one step (Cmd/Ctrl+])" className={cls} data-testid={`ad-editor-bring-forward-${testKey}`}>
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onReorder("front")} title="Bring to front" className={cls} data-testid={`ad-editor-bring-front-${testKey}`}>
        <ArrowUpToLine className="w-3.5 h-3.5" />
      </button>
    </>
  );
}

function MultiInspector({ count, onAlign, onDistribute, onDuplicate, onRemove, onReorder }) {
  return (
    <div className="p-4 space-y-3" data-testid="ad-editor-inspector-multi">
      <div className="flex items-center justify-between">
        <div className="overline">{count} elements selected</div>
        <div className="flex items-center gap-1">
          <button onClick={onDuplicate} title="Duplicate (Cmd/Ctrl+D)" className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]" data-testid="ad-editor-clone-multi">
            <Copy className="w-3.5 h-3.5" />
          </button>
          <OrderButtons onReorder={onReorder} testKey="multi" />
        </div>
      </div>
      <AlignRow onAlign={onAlign} toArtboard={false} />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
          Space evenly
        </div>
        <div className="grid grid-cols-2 gap-1">
          <button
            data-testid="ad-editor-distribute-x"
            disabled={count < 3}
            onClick={() => onDistribute("x")}
            className="text-[10px] px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-30"
          >
            Across
          </button>
          <button
            data-testid="ad-editor-distribute-y"
            disabled={count < 3}
            onClick={() => onDistribute("y")}
            className="text-[10px] px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-30"
          >
            Down
          </button>
        </div>
        {count < 3 && (
          <div className="text-[9px] text-[var(--ink-muted)] mt-1">Needs three or more.</div>
        )}
      </div>
      <button
        onClick={onRemove}
        data-testid="ad-editor-remove-multi"
        className="w-full text-[11px] inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-red-300 text-red-600 hover:opacity-70"
      >
        <Trash2 className="w-3 h-3" /> Remove {count} elements
      </button>
      <div className="text-[10px] text-[var(--ink-muted)]">
        Drag any one of them to move the group. Arrow keys nudge it.
      </div>
    </div>
  );
}

function ElementInspector({ el, ad, fontFamilies, onChange, onAdChange, onRemove, onReorder, onClone, onAlign }) {
  const kind = G.elementKind(el.key);
  const isLogo = kind === "logo";
  const isSponsorBar = kind === "bar";
  const isImage = kind === "image" || kind === "barImage";
  const isCustomText = kind === "customText";
  const isTextElement = kind === "text" || isCustomText;
  // Ad-field text and the logo are 1:1 with the ad, so a copy would
  // just draw the same thing twice.
  const canClone = G.canDuplicate(el.key);
  const adField = AD_FIELD_KEYS.includes(el.key) ? el.key : null;
  const families = fontFamilies || FALLBACK_FONT_FAMILIES;
  const locked = isLocked(el);
  const keep = G.keepsShape(el);
  const rect = G.rectOf(el);
  // The four boxes work in artboard pixels (what the rulers show);
  // the layout itself stays in percent.
  const setPx = (field, px) => {
    if (field === "x") {
      onChange({ x: Math.max(0, Math.min(100 - rect.w, G.pxToPctX(px))) });
    } else if (field === "y") {
      onChange({ y: Math.max(0, Math.min(100 - rect.h, G.pxToPctY(px))) });
    } else if (field === "w") {
      const r = G.resizeRect(rect, "se", G.pxToPctX(px) - rect.w, 0, { keepRatio: keep });
      onChange({ x: r.x, y: r.y, w: r.w, h: r.h });
    } else {
      const r = G.resizeRect(rect, "se", 0, G.pxToPctY(px) - rect.h, { keepRatio: keep });
      onChange({ x: r.x, y: r.y, w: r.w, h: r.h });
    }
  };
  return (
    <div className="p-4 space-y-3" data-testid={`ad-editor-inspector-${el.key}`}>
      <div className="flex items-center justify-between">
        <div className="overline">
          {labelFor(el.key)} · properties
        </div>
        <div className="flex items-center gap-1">
          {canClone && (
            <button
              onClick={onClone}
              data-testid={`ad-editor-clone-${el.key}`}
              title="Duplicate (Cmd/Ctrl+D)"
              className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          )}
          <OrderButtons onReorder={onReorder} testKey={el.key} />
          <button
            onClick={onRemove}
            disabled={locked}
            data-testid={`ad-editor-remove-${el.key}`}
            title={locked ? "Unlock to remove" : "Remove (Delete)"}
            className="ml-1 text-[10px] text-red-600 hover:text-red-800 disabled:opacity-40"
          >
            Remove
          </button>
        </div>
      </div>

      {locked && (
        <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1.5">
          Locked — it can't be moved or picked up on the artboard. Unlock it with the
          padlock in Layers.
        </div>
      )}

      {isTextElement && adField && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
            Text content
          </div>
          <input
            data-testid={`ad-editor-text-${el.key}`}
            value={ad[adField] || ""}
            onChange={(e) => onAdChange({ [adField]: e.target.value })}
            placeholder={`(${el.key})`}
            className="w-full text-sm border border-[var(--border)] px-2 py-1.5 bg-white outline-none focus:border-[var(--ink)]"
          />
          <div className="text-[10px] text-[var(--ink-muted)] mt-1">
            This is the ad's own {el.key} — it changes in every style.
          </div>
        </div>
      )}

      {isCustomText && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
            Text content
          </div>
          <textarea
            data-testid={`ad-editor-text-${el.key}`}
            value={el.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
            placeholder="Type anything…"
            rows={2}
            className="w-full text-sm border border-[var(--border)] px-2 py-1.5 bg-white outline-none focus:border-[var(--ink)] resize-y"
          />
          <div className="text-[10px] text-[var(--ink-muted)] mt-1">
            Free-form text — not tied to any ad field.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <NumField label="X (px)" value={G.pctToPxX(rect.x)} onChange={(v) => setPx("x", v)} testId={`ad-editor-x-${el.key}`} />
        <NumField label="Y (px)" value={G.pctToPxY(rect.y)} onChange={(v) => setPx("y", v)} testId={`ad-editor-y-${el.key}`} />
        <NumField label="Width (px)" value={G.pctToPxX(rect.w)} onChange={(v) => setPx("w", v)} testId={`ad-editor-w-${el.key}`} />
        <NumField label="Height (px)" value={G.pctToPxY(rect.h)} onChange={(v) => setPx("h", v)} testId={`ad-editor-h-${el.key}`} />
      </div>
      <label className="flex items-start gap-2 text-[11px] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          data-testid={`ad-editor-keep-shape-${el.key}`}
          checked={keep}
          onChange={(e) => onChange({ lock_ratio: e.target.checked })}
        />
        <span>
          Keep shape
          <span className="block text-[10px] text-[var(--ink-muted)]">
            Resizing keeps the proportions. Hold Shift while dragging a handle to do the
            opposite once.
          </span>
        </span>
      </label>

      <AlignRow onAlign={onAlign} toArtboard />

      {/* Sponsor bar-specific: color + fill mode */}
      {isSponsorBar && (
        <SponsorBarControls el={el} onChange={onChange} adBgColor={ad.background_color} />
      )}

      {/* Image / sponsor bar image: file upload + fit mode */}
      {isImage && (
        <ImageElementControls el={el} onChange={onChange} />
      )}

      {isLogo && (
        <div className="text-[10px] text-[var(--ink-muted)]">
          The logo is the one uploaded on the sponsor's own form. It always fits inside
          this box without being stretched.
        </div>
      )}

      {isTextElement && (
        <>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
                Font size ({el.font_px != null ? `${Number(el.font_px).toFixed(1)}%` : "auto"})
              </div>
              {el.font_px != null && (
                <button
                  data-testid={`ad-editor-font-px-auto-${el.key}`}
                  onClick={() => onChange({ font_px: null })}
                  className="text-[9px] text-[var(--ink-muted)] hover:text-[var(--ink)] underline"
                >
                  Reset to auto
                </button>
              )}
            </div>
            <input
              type="range"
              min="0.5"
              max="15"
              step="0.1"
              value={el.font_px != null ? el.font_px : Number(el.h) * 0.9}
              onChange={(e) => onChange({ font_px: parseFloat(e.target.value) })}
              data-testid={`ad-editor-font-px-${el.key}`}
              className="w-full accent-[var(--accent)]"
            />
            <div className="text-[9px] text-[var(--ink-muted)] mt-0.5">
              % of image height (higher = larger text)
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
              Font family
            </div>
            <select
              data-testid={`ad-editor-font-family-${el.key}`}
              value={el.font_family || "sans"}
              onChange={(e) => onChange({ font_family: e.target.value })}
              className="w-full text-xs border border-[var(--border)] px-2 py-1 bg-white"
              style={{
                fontFamily: (families.find((f) => f.key === el.font_family)?.stack) || undefined,
              }}
            >
              {families.map((f) => (
                <option
                  key={f.key}
                  value={f.key}
                  style={{ fontFamily: f.stack }}
                >
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
              Font weight
            </div>
            <select
              data-testid={`ad-editor-font-weight-${el.key}`}
              value={el.font_weight || "regular"}
              onChange={(e) => onChange({ font_weight: e.target.value })}
              className="w-full text-xs border border-[var(--border)] px-2 py-1 bg-white"
            >
              {FONT_WEIGHTS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
              Text color
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid={`ad-editor-color-${el.key}`}
                type="color"
                value={el.font_color && el.font_color !== "auto" ? el.font_color : "#ffffff"}
                onChange={(e) => onChange({ font_color: e.target.value })}
                className="h-8 w-10 border border-[var(--border)] bg-transparent cursor-pointer"
              />
              <button
                data-testid={`ad-editor-color-auto-${el.key}`}
                onClick={() => onChange({ font_color: "auto" })}
                className={
                  "text-[10px] px-2 py-1 border " +
                  (el.font_color === "auto" || !el.font_color
                    ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                    : "border-[var(--border)] hover:border-[var(--ink)]")
                }
              >
                Auto contrast
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
              Align
            </div>
            <div className="flex gap-1">
              {ALIGN_OPTIONS.map((a) => (
                <button
                  key={a}
                  data-testid={`ad-editor-align-${el.key}-${a}`}
                  onClick={() => onChange({ align: a })}
                  className={
                    "text-[10px] px-2 py-1 border capitalize " +
                    ((el.align || "left") === a
                      ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                      : "border-[var(--border)] hover:border-[var(--ink)]")
                  }
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Opacity + shadow controls — apply to every element type. */}
      <OpacityControl el={el} onChange={onChange} />
      <ShadowControls el={el} onChange={onChange} />
    </div>
  );
}

function OpacityControl({ el, onChange }) {
  const value = el.opacity == null ? 100 : Number(el.opacity);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display flex justify-between">
        <span>Opacity</span>
        <span>{Math.round(value)}%</span>
      </div>
      <input
        type="range"
        min="0" max="100" step="1"
        value={value}
        onChange={(e) => onChange({ opacity: parseFloat(e.target.value) })}
        data-testid={`ad-editor-opacity-${el.key}`}
        className="w-full accent-[var(--accent)]"
      />
    </div>
  );
}

function ShadowControls({ el, onChange }) {
  const shadow = el.shadow || null;
  const setShadow = (patch) =>
    onChange({ shadow: { ...(shadow || defaultShadow()), ...patch } });
  return (
    <div className="border-t border-[var(--border)] pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
          Drop shadow
        </div>
        <label className="text-[10px] flex items-center gap-1 cursor-pointer text-[var(--ink-muted)]">
          <input
            type="checkbox"
            data-testid={`ad-editor-shadow-toggle-${el.key}`}
            checked={!!shadow}
            onChange={(e) =>
              onChange({ shadow: e.target.checked ? defaultShadow() : null })
            }
          />
          Enable
        </label>
      </div>
      {shadow && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="color"
              data-testid={`ad-editor-shadow-color-${el.key}`}
              value={shadow.color || "#000000"}
              onChange={(e) => setShadow({ color: e.target.value })}
              className="h-7 w-9 border border-[var(--border)] bg-transparent cursor-pointer"
            />
            <div className="text-[10px] text-[var(--ink-muted)]">Color</div>
          </div>
          <div>
            <div className="text-[9px] text-[var(--ink-muted)] mb-0.5 flex justify-between">
              <span>Opacity</span><span>{Math.round(Number(shadow.opacity ?? 60))}%</span>
            </div>
            <input
              type="range" min="0" max="100" step="1"
              value={shadow.opacity ?? 60}
              onChange={(e) => setShadow({ opacity: parseFloat(e.target.value) })}
              data-testid={`ad-editor-shadow-opacity-${el.key}`}
              className="w-full accent-[var(--accent)]"
            />
          </div>
          <div>
            <div className="text-[9px] text-[var(--ink-muted)] mb-0.5 flex justify-between">
              <span>Blur (softness)</span><span>{Math.round(Number(shadow.blur ?? 12))}px</span>
            </div>
            <input
              type="range" min="0" max="40" step="1"
              value={shadow.blur ?? 12}
              onChange={(e) => setShadow({ blur: parseFloat(e.target.value) })}
              data-testid={`ad-editor-shadow-blur-${el.key}`}
              className="w-full accent-[var(--accent)]"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <NumField
              label="Offset X" value={shadow.offset_x ?? 0}
              onChange={(v) => setShadow({ offset_x: v })}
            />
            <NumField
              label="Offset Y" value={shadow.offset_y ?? 4}
              onChange={(v) => setShadow({ offset_y: v })}
            />
          </div>
          <div className="text-[9px] text-[var(--ink-muted)]">
            Offsets are in pixels (based on the final baked image, so 4px on a 1300-wide feed post ≈ 0.3%).
          </div>
        </div>
      )}
    </div>
  );
}

function defaultShadow() {
  return { color: "#000000", opacity: 60, blur: 12, offset_x: 0, offset_y: 4 };
}

function SponsorBarControls({ el, onChange, adBgColor }) {
  return (
    <>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
          Bar fill
        </div>
        <div className="flex gap-1">
          {["solid", "gradient", "none"].map((m) => (
            <button
              key={m}
              data-testid={`ad-editor-bar-mode-${m}`}
              onClick={() => onChange({ mode: m })}
              className={
                "text-[10px] px-2 py-1 border capitalize " +
                ((el.mode || "solid") === m
                  ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                  : "border-[var(--border)] hover:border-[var(--ink)]")
              }
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      {el.mode === "gradient" && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
            Direction
          </div>
          <select
            data-testid="ad-editor-bar-direction"
            value={el.direction || "up"}
            onChange={(e) => onChange({ direction: e.target.value })}
            className="w-full text-xs border border-[var(--border)] px-2 py-1 bg-white"
          >
            <option value="up">↑ Solid bottom → transparent top</option>
            <option value="down">↓ Solid top → transparent bottom</option>
            <option value="left">← Solid right → transparent left</option>
            <option value="right">→ Solid left → transparent right</option>
          </select>
        </div>
      )}
      {(el.mode || "solid") !== "none" && (
        <>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
              Bar color
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                data-testid="ad-editor-bar-color"
                value={el.color || adBgColor || "#111827"}
                onChange={(e) => onChange({ color: e.target.value })}
                className="h-8 w-10 border border-[var(--border)] bg-transparent cursor-pointer"
              />
              <button
                data-testid="ad-editor-bar-color-inherit"
                onClick={() => onChange({ color: null })}
                className={
                  "text-[10px] px-2 py-1 border " +
                  (!el.color
                    ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                    : "border-[var(--border)] hover:border-[var(--ink)]")
                }
              >
                Use ad color
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display flex justify-between">
              <span>Bar alpha</span>
              <span>{Math.round(Number(el.alpha ?? 92))}%</span>
            </div>
            <input
              type="range" min="0" max="100" step="1"
              value={el.alpha ?? 92}
              onChange={(e) => onChange({ alpha: parseFloat(e.target.value) })}
              data-testid="ad-editor-bar-alpha"
              className="w-full accent-[var(--accent)]"
            />
          </div>
        </>
      )}
    </>
  );
}

function ImageElementControls({ el, onChange }) {
  const inputRef = useRef(null);
  const upload = useMutation({
    mutationFn: async (file) => {
      const r = await api.uploadAdAsset(file);
      const size = await naturalSize(fileUrl(r.path));
      return { path: r.path, size };
    },
    onSuccess: ({ path, size }) => {
      // A plain image takes the picture's own shape straight away, so
      // it is never squashed or letterboxed inside a leftover box. A
      // sponsor-bar image keeps its box — those are sized on purpose.
      if (size && G.elementKind(el.key) === "image") {
        onChange({ src: path, ...G.fitToAspect(G.rectOf(el), size.w, size.h) });
      } else {
        onChange({ src: path });
      }
      toast.success("Image uploaded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const matchShape = useMutation({
    mutationFn: () => naturalSize(fileUrl(el.src)),
    onSuccess: (size) => {
      if (!size) {
        toast.error("Could not read that image's size");
        return;
      }
      onChange(G.fitToAspect(G.rectOf(el), size.w, size.h));
    },
  });
  return (
    <>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
          Source image
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 text-[10px] font-mono-display truncate text-[var(--ink-muted)]">
            {el.src ? el.src.split("/").pop() : "(none)"}
          </div>
          <button
            data-testid={`ad-editor-upload-${el.key}`}
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
          >
            {upload.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Upload className="w-3 h-3" />
            )}
            Upload
          </button>
          {/* `hotkey={false}` on purpose: Cmd/Ctrl+V on the artboard
              pastes a picture as a NEW image element. This button
              replaces the picture of the element you are editing. */}
          <PasteImageButton
            onImage={(file) => upload.mutate(file)}
            accept={ASSET_ACCEPT}
            busy={upload.isPending}
            hotkey={false}
            testId={`ad-editor-paste-${el.key}`}
            label="Paste"
            title="Paste an image from the clipboard into this element"
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
            iconClass="w-3 h-3"
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ASSET_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = "";
          }}
        />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
          Fit mode
        </div>
        <div className="flex flex-wrap gap-1">
          {["contain", "stretch"].map((m) => (
            <button
              key={m}
              data-testid={`ad-editor-fit-${el.key}-${m}`}
              onClick={() => onChange({ fit: m })}
              className={
                "text-[10px] px-2 py-1 border capitalize " +
                ((el.fit || "contain") === m
                  ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                  : "border-[var(--border)] hover:border-[var(--ink)]")
              }
            >
              {m}
            </button>
          ))}
          <button
            data-testid={`ad-editor-match-shape-${el.key}`}
            disabled={!el.src || matchShape.isPending}
            onClick={() => matchShape.mutate()}
            title="Reshape the box to the picture's own proportions"
            className="text-[10px] px-2 py-1 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40"
          >
            Match image shape
          </button>
        </div>
        <div className="text-[9px] text-[var(--ink-muted)] mt-1">
          Contain never distorts the picture. Stretch fills the box exactly.
        </div>
      </div>
    </>
  );
}

// ---------- FontsModal ---------------------------------------------------
function FontsModal({ onClose }) {
  const qc = useQueryClient();
  const { data: fonts = [], isLoading } = useQuery({
    queryKey: ["ad-fonts"],
    queryFn: () => api.listFonts(),
  });
  const [familyInput, setFamilyInput] = useState("");
  const [selectedWeights, setSelectedWeights] = useState({
    Regular: true, Bold: true, Italic: false, BoldItalic: false,
  });
  const install = useMutation({
    mutationFn: () => {
      const weights = Object.entries(selectedWeights)
        .filter(([, v]) => v)
        .map(([k]) => k);
      return api.installFont(familyInput.trim(), weights);
    },
    onSuccess: (r) => {
      toast.success(
        `Installed ${r.installed.length} weight(s) of ${r.family}` +
        (r.skipped.length ? ` · ${r.skipped.length} not available` : ""),
      );
      setFamilyInput("");
      qc.invalidateQueries({ queryKey: ["ad-fonts"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Install failed"),
  });
  const remove = useMutation({
    mutationFn: (family) => api.deleteFont(family),
    onSuccess: () => {
      toast.success("Font removed");
      qc.invalidateQueries({ queryKey: ["ad-fonts"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="ad-editor-fonts-modal"
        className="w-full max-w-lg bg-white border border-[var(--border)] shadow-xl"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">Style editor</div>
            <h2 className="font-serif-display text-lg">Fonts</h2>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="overline mb-2">Install a Google Font</div>
            <div className="flex items-center gap-2 mb-2">
              <input
                data-testid="ad-editor-fonts-family-input"
                value={familyInput}
                onChange={(e) => setFamilyInput(e.target.value)}
                placeholder="e.g. Poppins, Playfair Display"
                className="flex-1 text-sm border border-[var(--border)] px-2 py-1.5 outline-none focus:border-[var(--ink)]"
              />
              <button
                data-testid="ad-editor-fonts-install-btn"
                onClick={() => install.mutate()}
                disabled={!familyInput.trim() || install.isPending}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate-900)] text-white disabled:opacity-40"
              >
                {install.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Plus className="w-3 h-3" />
                )}
                Install
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {["Regular", "Bold", "Italic", "BoldItalic"].map((w) => (
                <label
                  key={w}
                  className="text-[11px] inline-flex items-center gap-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!selectedWeights[w]}
                    onChange={(e) =>
                      setSelectedWeights((prev) => ({ ...prev, [w]: e.target.checked }))
                    }
                  />
                  {w}
                </label>
              ))}
            </div>
            <div className="mt-1 text-[10px] text-[var(--ink-muted)]">
              Pulled from the canonical google/fonts GitHub repo. Some
              families ship variable-only files — only Regular installs
              in that case.
            </div>
          </div>
          <div>
            <div className="overline mb-2">Installed</div>
            {isLoading ? (
              <div className="text-xs text-[var(--ink-muted)] flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            ) : (
              <ul className="space-y-1 max-h-64 overflow-y-auto" data-testid="ad-editor-fonts-list">
                {fonts.map((f) => (
                  <li
                    key={f.family}
                    className="flex items-center gap-2 border border-[var(--border)] px-2 py-1.5"
                  >
                    <span
                      className="flex-1 text-sm"
                      style={{ fontFamily: `'${f.family}', system-ui` }}
                    >
                      {f.family}
                    </span>
                    <span className="text-[10px] font-mono-display text-[var(--ink-muted)]">
                      {f.weights.join(" · ")}
                    </span>
                    {!f.family.toLowerCase().startsWith("liberation") && (
                      <button
                        data-testid={`ad-editor-fonts-delete-${f.family}`}
                        onClick={() => {
                          if (window.confirm(`Remove ${f.family}?`)) {
                            remove.mutate(f.family);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Number box that edits live but never fights the typist: while the
// field has focus it shows exactly what was typed, and re-formats only
// on blur. (The old one re-formatted on every keystroke, so typing "12"
// came out as "1.0".)
function NumField({ label, value, onChange, step = 1, digits = 0, testId }) {
  const shown = Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : "0";
  const [draft, setDraft] = useState(null);
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-display">
        {label}
      </div>
      <input
        type="number"
        step={step}
        data-testid={testId}
        value={draft === null ? shown : draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
        }}
        className="w-full text-xs border border-[var(--border)] px-2 py-1 bg-white font-mono-display"
      />
    </label>
  );
}

function makeDefaultElement(key, uniqueKey = null) {
  // For image + sponsor_bar_image + text the caller may supply a
  // unique key suffix so multiple can coexist in one layout.
  if (key === "logo") return { key, x: 2, y: 88, w: 8, h: 10 };
  if (key === "text") {
    return {
      key: uniqueKey || key,
      x: 40, y: 45, w: 20, h: 6,
      font_family: "sans", font_weight: "regular",
      font_color: "#ffffff", align: "center",
      text: "New text",
    };
  }
  if (key === "sponsor_bar") {
    return {
      key,
      x: 0, y: 85, w: 100, h: 15,
      mode: "solid",
      color: null,          // null = use ad.background_color
      alpha: 92,
    };
  }
  if (key === "sponsor_bar_image") {
    return {
      key: uniqueKey || key,
      x: 0, y: 85, w: 100, h: 15,
      src: "",
      fit: "stretch",       // sponsor bars almost always want full-width
    };
  }
  if (key === "image") {
    return {
      key: uniqueKey || key,
      x: 40, y: 40, w: 20, h: 20,
      src: "",
      fit: "contain",
    };
  }
  const base = {
    key, x: 12, y: 88, w: 40, h: 5,
    font_family: "sans", font_weight: key === "title" ? "bold" : "regular",
    font_color: "auto", align: "left",
  };
  if (key === "subhead") return { ...base, y: 94, h: 4 };
  if (key === "phone")   return { ...base, x: 58, y: 90, w: 40, h: 3.5, align: "right" };
  if (key === "website") return { ...base, x: 58, y: 93.5, w: 40, h: 3.5, align: "right" };
  if (key === "email")   return { ...base, x: 58, y: 96.5, w: 40, h: 3.5, align: "right" };
  return base;
}
